import { SLoader } from "@avalara/skylab-sdk/react";
import map from "lodash/map";
import { Suspense } from "react";
import { matchPath } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { ERROR_PAGE_TYPES } from "../../shared/constants";
import ErrorPage from "../errorHandling/ErrorPage";
import commonRoutesConfig from "./commonRoutesConfig";
import Protected from "./ProtectedRoutes";
import retailRoutesConfig from "./retailRoutesConfig";

/**
 * Recursively constructs a route configuration by wrapping route elements
 * in a <Protected> component if accessConfig is provided, and assigns a unique ID to each route.
 *
 * @param {Object} routesConfig - The initial route configuration object.
 * Each route object may contain:
 * - `accessConfig`: (optional) Access control settings for the route.
 * - `element`: The React element to render for the route.
 * - `children`: (optional) Nested route configurations.
 * - `breadCrumbs`: (optional) Array of breadcrumb items for the route.
 *
 * @returns {Array<Object>} The processed route configuration with access protection and unique IDs applied.
 */
const constructRoutes = routesConfig => {
    return map(routesConfig, ({ accessConfig, breadCrumbs, ...route }) => {
        if (route.children) route.children = constructRoutes(route.children);
        route.element = (
            <Suspense fallback={<SLoader loading="true" aria-live="polite" />}>
                {route.element}
            </Suspense>
        );
        if (accessConfig)
            route.element = <Protected accessConfig={accessConfig}>{route.element}</Protected>;
        return {
            id: uuidv4(),
            breadCrumbs,
            ...route,
        };
    });
};

const errorHandlingRoutes = [
    {
        id: uuidv4(),
        path: "*",
        element: <ErrorPage type={ERROR_PAGE_TYPES.PAGE_NOT_FOUND} />,
    },
];

export const allRoutes = [
    ...constructRoutes(commonRoutesConfig),
    ...constructRoutes(retailRoutesConfig),
    ...errorHandlingRoutes,
];

// Root path must not perform an automatic redirect (AvaApp client-app contract, and
// AvaApp's automated tests land on "/"), so retail-only users are served the retail
// landing page's own element/access-config directly at "/" instead of being navigated to "/retail".
export const retailOnlyRoutes = [
    ...constructRoutes({
        retailLanding: {
            path: "/",
            accessConfig: retailRoutesConfig.retail.accessConfig,
            element: retailRoutesConfig.retail.element,
            children: { index: retailRoutesConfig.retail.children.index },
        },
    }),
    ...constructRoutes(retailRoutesConfig),
    ...errorHandlingRoutes,
];

/**
 * Applies dynamic breadcrumb modifications based on the referrer path.
 * Handles special cases where breadcrumbs should change based on navigation context.
 *
 * @param {Object} route - The matched route configuration
 * @param {string} pathname - Current location pathname
 * @param {string} referrer - Previous location pathname
 * @returns {Object} Route configuration with modified breadcrumbs if applicable
 */
const applyDynamicBreadcrumbs = (route, pathname, referrer) => {
    if (!route || !route.breadCrumbs) {
        return route;
    }

    const modifiedRoute = { ...route };

    if (pathname === "/requests/create" || pathname === "/reports/saved-searches") {
        if (referrer === "/search/certificates") {
            const breadcrumbs = [...route.breadCrumbs];
            breadcrumbs[1] = {
                ...breadcrumbs[1],
                label: "Certificates",
                url: "/search/certificates",
            };
            modifiedRoute.breadCrumbs = breadcrumbs;
        }
    }

    if (pathname.startsWith("/review")) {
        const breadcrumbs = [...route.breadCrumbs];
        if (pathname === "/review") {
            breadcrumbs[2] = {
                ...breadcrumbs[2],
                label: "Add a certificate",
                url: "/search/pending",
            };
        }
        // When redirected from a certificate detail page (which came from certificate search),
        // show "Customer certificates" breadcrumb instead of "Incoming certificates to review"
        if (referrer?.startsWith("/certificate/")) {
            breadcrumbs[1] = {
                ...breadcrumbs[1],
                label: "Customer certificates",
                url: "/search/certificates",
            };
        }
        modifiedRoute.breadCrumbs = breadcrumbs;
    }

    if (pathname.startsWith("/certificate")) {
        const breadcrumbs = [...route.breadCrumbs];
        if (referrer === "/search/vendor-certificates") {
            breadcrumbs[1] = {
                ...breadcrumbs[1],
                label: "Vendor certificates",
                url: "/search/vendor-certificates",
            };
            modifiedRoute.breadCrumbs = breadcrumbs;
        }
    }

    return modifiedRoute;
};

/**
 * Recursively finds the route configuration that matches the given pathname.
 * Returns the matching route with its breadCrumbs and other custom properties.
 * Prioritizes more specific matches over less specific ones.
 * Dynamically updates breadcrumbs based on referrer for specific routes.
 *
 * @param {Array} routesArray - Array of route configuration objects
 * @param {string} pathname - Current location pathname to match against
 * @param {string} referrer - Previous location pathname for breadcrumb context
 * @returns {Object|null} The matching route configuration or null if not found
 */
export const findActiveRoute = (routesArray, pathname, referrer = null) => {
    let bestMatch = null;
    let bestMatchPathLength = -1;

    for (const route of routesArray) {
        if (route.path) {
            const match = matchPath({ path: route.path, end: !route.children }, pathname);
            if (match) {
                // Track the current match
                const currentPathLength = route.path.length;

                // Check children for a more specific match
                if (route.children) {
                    const childMatch = findActiveRoute(route.children, pathname, referrer);
                    if (childMatch) {
                        // Inherit parent breadCrumbs if child doesn't have any
                        const matchWithBreadcrumbs = childMatch.breadCrumbs
                            ? childMatch
                            : { ...childMatch, breadCrumbs: route.breadCrumbs };

                        // Apply dynamic breadcrumb modifications before returning
                        return applyDynamicBreadcrumbs(matchWithBreadcrumbs, pathname, referrer);
                    }
                }

                // Keep this match if it's more specific than previous matches
                if (currentPathLength > bestMatchPathLength) {
                    bestMatch = route;
                    bestMatchPathLength = currentPathLength;
                }
            }
        } else if (route.children) {
            // Only check children if parent has no path (index routes / layout routes)
            const childMatch = findActiveRoute(route.children, pathname, referrer);
            if (childMatch) return applyDynamicBreadcrumbs(childMatch, pathname, referrer);
        }
    }

    // Apply dynamic breadcrumb modifications based on referrer
    if (bestMatch) {
        bestMatch = applyDynamicBreadcrumbs(bestMatch, pathname, referrer);
    }

    return bestMatch;
};
